import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList
} from "@/components/ui/command";
import { Check, ChevronsUpDown, Building2 } from "lucide-react";

// Searchable combobox to pick a saved customer and auto-fill quote fields.
export const CustomerSelect = ({ value, onSelect, refreshKey = 0 }) => {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/customers");
      setCustomers(data);
    } catch {
      /* silent — selecting a saved customer is optional */
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between rounded-sm font-normal"
          data-testid="customer-select-trigger"
        >
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0 text-[#1E40AF]" />
            <span className="truncate">
              {value ? value : "Select a saved customer…"}
            </span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 rounded-sm" align="start">
        <Command>
          <CommandInput placeholder="Search customers…" data-testid="customer-select-search" />
          <CommandList>
            <CommandEmpty>No saved customers found.</CommandEmpty>
            <CommandGroup>
              {customers.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.place || ""} ${c.contact || ""}`}
                  onSelect={() => {
                    onSelect(c);
                    setOpen(false);
                  }}
                  data-testid={`customer-option-${c.id}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c.name ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span className="font-medium">{c.name}</span>
                    <span className="text-[11px] text-slate-500">
                      {[c.place, c.contact].filter(Boolean).join(" • ") || "—"}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CustomerSelect;
